import { MoreOutlined } from '@ant-design/icons'
import { Button, Dropdown, Menu, Tag } from 'antd'
import { ColumnFilterItem } from 'antd/lib/table/interface'
import React from 'react'

import { renderTenantLink } from '../../../components/tables/table-column-renderers'
import {
  buildSearchFilterProps,
  sortBooleanColumn,
  sortNumericColumn,
  sortStringColumn,
} from '../../../components/tables/utils'
import { Tenant } from '../models'

interface Props {
  onUpdate: (id: number) => void
  onDetail: (id: number) => void
  onDelete: (tenant: Tenant) => void
  onUpdateRatesheet: (tenant: Tenant) => void
  onUpdateLicenses: (id: number) => void
  tableFilterMenuItems: {
    segmentCodes: ColumnFilterItem[]
  }
}

const renderActionsMenu =
  ({
    onDetail,
    onDelete,
    onUpdate,
    onUpdateRatesheet,
    onUpdateLicenses,
  }: {
    onDetail: (tenantId: number) => void
    onDelete: (tenant: Tenant) => void
    onUpdate?: (tenantId: number) => void
    onUpdateRatesheet: (tenant: Tenant) => void
    onUpdateLicenses?: (tenantId: number) => void
  }) =>
  (tenant: Tenant) => {
    const menu = (
      <Menu key={tenant.id}>
        <Menu.Item key="tenantDetails" onClick={() => onDetail(tenant.id)}>
          Tenant Details
        </Menu.Item>
        <Menu.Item key="updateTenant" onClick={() => onUpdate(tenant.id)}>
          Update Tenant
        </Menu.Item>
        <Menu.Item
          key="updateRatesheet"
          onClick={() => onUpdateRatesheet(tenant)}
          disabled={tenant.segmentCode !== 'BANK'}
        >
          Update Ratesheet
        </Menu.Item>
        <Menu.Item key="updateLicenses" onClick={() => onUpdateLicenses(tenant.id)}>
          Update Licenses
        </Menu.Item>
        <Menu.Item key="deleteTenant" onClick={() => onDelete(tenant)}>
          Delete Tenant
        </Menu.Item>
      </Menu>
    )

    return (
      <Dropdown placement="bottomRight" overlay={menu}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    )
  }

export const buildTenantsTableColumns = ({
  onUpdate,
  onDetail,
  onDelete,
  onUpdateRatesheet,
  onUpdateLicenses,
  tableFilterMenuItems,
}: Props) => [
  {
    title: 'Id',
    dataIndex: 'id',
    sorter: sortNumericColumn({ dataIndex: 'id' }),
    ...buildSearchFilterProps({ dataIndex: 'id' }),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    sorter: sortStringColumn({ dataIndex: 'name' }),
    ...buildSearchFilterProps({ dataIndex: 'name' }),
  },
  {
    title: 'Domain',
    dataIndex: 'domain',
    sorter: sortStringColumn({ dataIndex: 'domain' }),
    ...buildSearchFilterProps({
      dataIndex: 'domain',
      render: renderTenantLink,
    }),
  },
  {
    title: 'Trial',
    dataIndex: 'isTrial',
    render: (value: boolean) => value && <Tag>Trial</Tag>,
    sorter: sortBooleanColumn({ dataIndex: 'isTrial' }),
  },
  {
    title: 'Demo',
    dataIndex: 'isDemo',
    render: (value: boolean) => value && <Tag>Demo</Tag>,
    sorter: sortBooleanColumn({ dataIndex: 'isDemo' }),
  },
  {
    title: 'SoD',
    dataIndex: 'enforceSeparationOfDuties',
    render: (value: boolean) => value && <Tag>SoD</Tag>,
    sorter: sortBooleanColumn({ dataIndex: 'enforceSeparationOfDuties' }),
  },
  {
    title: 'T+N',
    dataIndex: 'defaultTPlusNDays',
    sorter: sortNumericColumn({ dataIndex: 'defaultTPlusNDays' }),
  },
  { title: 'Currency', dataIndex: 'currency', sorter: sortStringColumn({ dataIndex: 'currency' }) },
  {
    title: 'Segment',
    dataIndex: 'segmentCode',
    filters: tableFilterMenuItems.segmentCodes,
    onFilter: (segmentCode: string, record: Tenant) => record.segmentCode.indexOf(segmentCode) === 0,
  },
  {
    title: 'Issuer ID',
    dataIndex: 'issuerId',
    sorter: sortNumericColumn({ dataIndex: 'issuerId' }),
  },
  {
    title: 'National Code (ABN)',
    dataIndex: 'nationalCode',
    sorter: sortStringColumn({ dataIndex: 'nationalCode' }),
    ...buildSearchFilterProps({ dataIndex: 'nationalCode' }),
  },
  { title: '', render: renderActionsMenu({ onUpdate, onDetail, onDelete, onUpdateRatesheet, onUpdateLicenses }) },
]
